import React, { useState, useEffect } from "react";
import { getAbout, updateAbout, getDelegates, updateDelegate, uploadFile, getUploadUrl } from "../api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Separator } from "../components/ui/separator";
import { Skeleton } from "../components/ui/skeleton";
import { Save, Upload, Check, Mail, User } from "lucide-react";
import { toast } from "sonner";

export const AdminAbout = () => {
  const [aboutText, setAboutText] = useState("");
  const [delegates, setDelegates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingAbout, setSavingAbout] = useState(false);
  const [savingDelegate, setSavingDelegate] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [aboutData, delegatesData] = await Promise.all([
          getAbout(),
          getDelegates(),
        ]);
        setAboutText(aboutData.text || "");
        setDelegates(delegatesData || []);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Erreur lors du chargement des données");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSaveAbout = async () => {
    setSavingAbout(true);
    try {
      await updateAbout(aboutText);
      toast.success("Texte de présentation mis à jour");
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSavingAbout(false);
    }
  };

  const handleDelegateChange = (id, field, value) => {
    setDelegates((prev) =>
      prev.map((d) => (d.id === id ? { ...d, [field]: value } : d))
    );
  };

  const handleSaveDelegate = async (delegate) => {
    setSavingDelegate(delegate.id);
    try {
      await updateDelegate(delegate.id, {
        name: delegate.name,
        role: delegate.role,
        email: delegate.email,
        photo: delegate.photo,
        bio: delegate.bio,
      });
      toast.success(`Profil de ${delegate.name} mis à jour`);
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSavingDelegate(null);
    }
  };

  const handlePhotoUpload = async (delegateId, file) => {
    try {
      const result = await uploadFile(file);
      const photoUrl = getUploadUrl(result.url);
      handleDelegateChange(delegateId, "photo", photoUrl);
      toast.success("Photo uploadée");
    } catch (error) {
      toast.error("Erreur lors de l'upload");
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-48 w-full" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* About Text */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg text-slate-800">
            Texte de présentation
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            value={aboutText}
            onChange={(e) => setAboutText(e.target.value)}
            rows={10}
            className="resize-y font-normal text-sm"
            placeholder="Décrivez votre section syndicale..."
          />
          <p className="text-xs text-slate-400">
            Séparez les paragraphes par une ligne vide.
          </p>
          <button
            onClick={handleSaveAbout}
            disabled={savingAbout}
            className="inline-flex items-center gap-2 px-4 py-2 bg-sky-500 text-white text-sm font-medium rounded-lg hover:bg-sky-600 transition-colors disabled:opacity-50"
          >
            {savingAbout ? (
              <span>Sauvegarde...</span>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Sauvegarder
              </>
            )}
          </button>
        </CardContent>
      </Card>

      <Separator />

      {/* Delegates */}
      <div>
        <h2 className="text-lg font-semibold text-slate-800 mb-4">
          Délégués syndicaux
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {delegates.map((delegate) => (
            <Card key={delegate.id} className="border-0 shadow-sm">
              <CardContent className="p-6 space-y-4">
                {/* Photo */}
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <img
                      src={delegate.photo || "https://via.placeholder.com/80"}
                      alt={delegate.name}
                      className="w-20 h-20 rounded-full object-cover border-2 border-slate-100"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-100 text-slate-600 text-xs font-medium rounded-lg hover:bg-slate-200 transition-colors cursor-pointer">
                      <Upload className="w-3.5 h-3.5" />
                      Changer la photo
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          if (e.target.files[0]) {
                            handlePhotoUpload(delegate.id, e.target.files[0]);
                          }
                        }}
                      />
                    </label>
                    <p className="text-xs text-slate-400 mt-1">JPG, PNG, WebP</p>
                  </div>
                </div>

                {/* Fields */}
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-slate-500">Nom</Label>
                    <Input
                      value={delegate.name}
                      onChange={(e) =>
                        handleDelegateChange(delegate.id, "name", e.target.value)
                      }
                      placeholder="Nom complet"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Rôle</Label>
                    <Input
                      value={delegate.role}
                      onChange={(e) =>
                        handleDelegateChange(delegate.id, "role", e.target.value)
                      }
                      placeholder="Délégué Syndical"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Email</Label>
                    <Input
                      type="email"
                      value={delegate.email}
                      onChange={(e) =>
                        handleDelegateChange(delegate.id, "email", e.target.value)
                      }
                      placeholder="email@example.fr"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Biographie</Label>
                    <Textarea
                      value={delegate.bio}
                      onChange={(e) =>
                        handleDelegateChange(delegate.id, "bio", e.target.value)
                      }
                      rows={2}
                      className="resize-y text-sm"
                      placeholder="Courte biographie..."
                    />
                  </div>
                </div>

                <button
                  onClick={() => handleSaveDelegate(delegate)}
                  disabled={savingDelegate === delegate.id}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-sky-500 text-white text-sm font-medium rounded-lg hover:bg-sky-600 transition-colors disabled:opacity-50"
                >
                  {savingDelegate === delegate.id ? (
                    "Sauvegarde..."
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      Sauvegarder
                    </>
                  )}
                </button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};
